import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, UserPlus, Blocks, X } from 'lucide-react';
import { api } from '../api';
import { cx } from './ui';

type Mode = 'csv' | 'manual' | 'integration';
type ImportResult = {
  created: number;
  updated: number;
  errors: { index: number; message: string }[];
};
interface RowInput {
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  location?: string;
}

// Minimal CSV → rows. Maps header columns email/first_name/last_name/role/location (case-insensitive).
// Handles simple quoted fields. Server does per-row validation + reports malformed rows.
function parseCsv(text: string): RowInput[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const splitRow = (line: string) =>
    (line.match(/("([^"]|"")*"|[^,]*)(,|$)/g) ?? [])
      .slice(0, -1)
      .map((c) => c.replace(/,$/, '').trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
  const header = splitRow(lines[0]!).map((h) => h.toLowerCase());
  const col = (cells: string[], name: string) => {
    const i = header.indexOf(name);
    return i >= 0 ? (cells[i] ?? '').trim() : '';
  };
  return lines.slice(1).map((line) => {
    const cells = splitRow(line);
    const row: RowInput = { email: col(cells, 'email') };
    const fn = col(cells, 'first_name') || col(cells, 'first name');
    const ln = col(cells, 'last_name') || col(cells, 'last name');
    const role = col(cells, 'role');
    const loc = col(cells, 'location');
    if (fn) row.first_name = fn;
    if (ln) row.last_name = ln;
    if (role) row.role = role;
    if (loc) row.location = loc;
    return row;
  });
}

export function ImportPeopleModal({
  orgId,
  onClose,
  onImported,
}: {
  orgId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('csv');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [csvRows, setCsvRows] = useState<RowInput[] | null>(null);
  const [manual, setManual] = useState<RowInput>({ email: '' });

  async function submit(recipients: RowInput[]) {
    if (recipients.length === 0) {
      setError('Nothing to import.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ImportResult>('/v1/recipients/import', {
        org_id: orgId,
        recipients,
      });
      setResult(res);
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  const tabs: { key: Mode; label: string; icon: typeof FileText }[] = [
    { key: 'csv', label: 'CSV upload', icon: FileText },
    { key: 'manual', label: 'Manual entry', icon: UserPlus },
    { key: 'integration', label: 'Integration', icon: Blocks },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/20" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-xl border border-line bg-paper shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="flex items-center gap-2 font-semibold text-ink">
            <Upload className="h-4 w-4" /> Import people
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1 border-b border-line px-5">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setMode(t.key);
                setResult(null);
                setError(null);
              }}
              className={cx(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium',
                mode === t.key
                  ? 'border-ink text-ink'
                  : 'border-transparent text-muted hover:text-ink',
              )}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-4 px-5 py-5">
          {result ? (
            <div className="space-y-2 text-sm">
              <div className="text-ink">
                Imported — <span className="font-medium">{result.created} created</span>,{' '}
                <span className="font-medium">{result.updated} updated</span>.
              </div>
              {result.errors.length > 0 && (
                <div className="rounded-input border border-line bg-ink/[0.02] px-3 py-2 text-muted">
                  {result.errors.length} row(s) skipped:
                  <ul className="mt-1 list-disc pl-5">
                    {result.errors.slice(0, 8).map((e) => (
                      <li key={e.index}>
                        row {e.index + 1}: {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                onClick={onClose}
                className="rounded-input border border-line px-3 py-1.5 text-sm font-medium text-ink hover:bg-ink/[0.04]"
              >
                Done
              </button>
            </div>
          ) : mode === 'csv' ? (
            <div className="space-y-3 text-sm">
              <p className="text-muted">
                CSV with a header row. Columns: <code>email</code> (required),{' '}
                <code>first_name</code>, <code>last_name</code>, <code>role</code>,{' '}
                <code>location</code>.
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={async (ev) => {
                  const file = ev.target.files?.[0];
                  if (!file) return;
                  setCsvRows(parseCsv(await file.text()));
                  setError(null);
                }}
                className="block w-full text-sm text-muted file:mr-3 file:rounded-input file:border file:border-line file:bg-paper file:px-3 file:py-1.5 file:text-sm file:text-ink"
              />
              {csvRows && <p className="text-muted">{csvRows.length} row(s) parsed.</p>}
              <button
                disabled={busy || !csvRows || csvRows.length === 0}
                onClick={() => csvRows && submit(csvRows)}
                className="rounded-input bg-ink px-4 py-2 text-sm font-medium text-paper disabled:opacity-50"
              >
                {busy ? 'Importing…' : 'Import CSV'}
              </button>
            </div>
          ) : mode === 'manual' ? (
            <form
              className="space-y-3 text-sm"
              onSubmit={(e) => {
                e.preventDefault();
                if (manual.email.trim()) submit([manual]);
              }}
            >
              {(
                [
                  ['email', 'Email *'],
                  ['first_name', 'First name'],
                  ['last_name', 'Last name'],
                  ['role', 'Role'],
                  ['location', 'Location'],
                ] as [keyof RowInput, string][]
              ).map(([field, label]) => (
                <label key={field} className="block">
                  <span className="mb-1 block text-xs text-muted">{label}</span>
                  <input
                    value={manual[field] ?? ''}
                    onChange={(e) => setManual((m) => ({ ...m, [field]: e.target.value }))}
                    type={field === 'email' ? 'email' : 'text'}
                    required={field === 'email'}
                    className="w-full rounded-input border border-line bg-paper px-3 py-1.5 text-ink"
                  />
                </label>
              ))}
              <button
                type="submit"
                disabled={busy || !manual.email.trim()}
                className="rounded-input bg-ink px-4 py-2 text-sm font-medium text-paper disabled:opacity-50"
              >
                {busy ? 'Adding…' : 'Add person'}
              </button>
            </form>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-muted">
                Sync People automatically from a directory (e.g. Microsoft Entra / AD). Connect a
                directory integration, then re-sync keeps this list current.
              </p>
              <button
                onClick={() => navigate('/integrations')}
                className="rounded-input bg-ink px-4 py-2 text-sm font-medium text-paper"
              >
                Go to Integrations
              </button>
            </div>
          )}

          {error && <p className="text-sm text-muted">{error}</p>}
        </div>
      </div>
    </div>
  );
}
